import type { z } from "zod/v4";
import type { VizAuthoringIssue } from "@/lib/ai/visualization/types";
import type { barRankingAuthoringSchema } from "@/lib/ai/visualization/views/bar-ranking/schema";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import { resolveBarLayout } from "@/lib/ai/visualization/views/shared/bar-layout";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "@/lib/ai/visualization/views/shared/element-builder";
import { resolvePreferredRankingSort } from "@/lib/ai/visualization/views/shared/presentation-defaults";
import {
  pushProjectableMetricBindingIssue,
  pushRequiredTextIssue,
  validateFieldRole,
} from "@/lib/ai/visualization/views/shared/spec-validation";
import type {
  ValidateVizSpecArgs,
  VizAuthoringCardDraft,
} from "@/lib/ai/visualization/views/shared/view-definition";

type BarRankingSpec = Extract<VizSpec, { type: "bar-ranking" }>;
type BarRankingAuthoring = z.infer<typeof barRankingAuthoringSchema>;

/**
 * ranking chart の compiled spec が dataset role と label contract を満たすか検証する。
 */
export function validateBarRankingSpec(
  args: ValidateVizSpecArgs<BarRankingSpec>,
): VizAuthoringIssue[] {
  const issues: VizAuthoringIssue[] = [];
  validateFieldRole({
    issues,
    dataset: args.dataset,
    path: "spec.categoryKey",
    fieldName: args.spec.categoryKey,
    role: "dimension",
  });
  validateFieldRole({
    issues,
    dataset: args.dataset,
    path: "spec.valueKey",
    fieldName: args.spec.valueKey,
    role: "metric",
  });
  pushProjectableMetricBindingIssue({
    issues,
    dataset: args.dataset,
    path: "spec.categoryKey",
    categoryKeys: [args.spec.categoryKey],
    metricKeys: [args.spec.valueKey],
  });
  pushRequiredTextIssue({
    issues,
    path: "spec.componentProps.categoryLabel",
    value: args.spec.componentProps?.categoryLabel ?? null,
    message:
      "categoryLabel is required because ranking chart labels are owned by the authoring output",
  });
  pushRequiredTextIssue({
    issues,
    path: "spec.componentProps.valueLabel",
    value: args.spec.componentProps?.valueLabel ?? null,
    message:
      "valueLabel is required because ranking chart labels are owned by the authoring output",
  });
  return issues;
}

/**
 * structured authoring payload を `bar-ranking` 用の renderer-ready spec へ変換する。
 */
export function compileBarRankingSpec(args: {
  datasetId: string;
  dataset: BuildVizElementArgs<BarRankingSpec>["dataset"];
  card: VizAuthoringCardDraft | null;
  view: BarRankingAuthoring;
}): BarRankingSpec {
  return {
    type: "bar-ranking",
    datasetId: args.datasetId,
    cardTitle: args.card?.title ?? null,
    categoryKey: args.view.categoryKey,
    valueKey: args.view.valueKey,
    limit: args.view.limit ?? null,
    sort: resolvePreferredRankingSort({
      dataset: args.dataset,
      valueKey: args.view.valueKey,
      requestedSort: args.view.sort,
    }),
    componentProps: {
      categoryLabel: args.view.categoryLabel,
      valueLabel: args.view.valueLabel,
      // top/bottom N card copy should stay stable instead of allowing a local sort inversion.
      allowSortControl: args.view.limit == null ? undefined : false,
    },
  };
}

/**
 * bar-ranking 用の UI element を構築する。
 */
export function buildBarRankingElement(
  args: BuildVizElementArgs<BarRankingSpec>,
): BuiltVizElement {
  return {
    elementKey: args.chartKey,
    element: {
      type: "BarRanking",
      props: {
        dataPath: args.dataPath,
        categoryKey: args.spec.categoryKey,
        valueKey: args.spec.valueKey,
        limit: args.spec.limit,
        layout: resolveBarLayout({
          dataset: args.dataset,
          categoryKey: args.spec.categoryKey,
        }),
        sort: args.spec.sort,
        ...(args.spec.componentProps ?? {}),
      },
      children: [],
    },
  };
}
